import { useCallback } from 'react';
import axiosInstance from '../api/axios';
import { API_ENDPOINTS } from '../api/endpoints';
import {
  TeamMember, PaginationInfo, CreateTeamMemberRequest, UpdateTeamMemberRequest,
} from '../api/teamTypes';

/** Team CRUD — wraps the /team endpoints (see plan §2.5). */
const BASE = API_ENDPOINTS.GET_TEAM_MEMBERS; // '/api/v1/team'

export interface TeamPage { data: TeamMember[]; pagination: PaginationInfo | null; }

export const useTeam = () => {
  const listTeam = useCallback(async (page = 1, limit = 50): Promise<TeamPage> => {
    const res = await axiosInstance.get(`${BASE}?page=${page}&limit=${limit}`);
    return { data: res.data?.data ?? [], pagination: res.data?.pagination ?? null };
  }, []);

  const searchTeam = useCallback(async (search: string, page = 1, limit = 20): Promise<TeamMember[]> => {
    const qs = new URLSearchParams({ search, page: String(page), limit: String(limit) });
    const res = await axiosInstance.get(`${BASE}/search?${qs.toString()}`);
    return res.data?.data ?? [];
  }, []);

  const getMember = useCallback(async (id: string): Promise<TeamMember> => {
    const res = await axiosInstance.get(`${BASE}/${id}`);
    return res.data.data;
  }, []);

  const createMember = useCallback(async (payload: CreateTeamMemberRequest): Promise<TeamMember> => {
    const res = await axiosInstance.post(BASE, payload);
    return res.data?.data?.team_member ?? res.data?.data;
  }, []);

  const updateMember = useCallback(async (id: string, payload: UpdateTeamMemberRequest): Promise<TeamMember> => {
    const res = await axiosInstance.put(`${BASE}/${id}`, payload);
    return res.data?.data?.team_member ?? res.data?.data;
  }, []);

  const toggleStatus = useCallback(async (id: string): Promise<void> => {
    await axiosInstance.patch(`${BASE}/${id}/toggle-status`);
  }, []);

  const deleteMember = useCallback(async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE}/${id}`);
  }, []);

  return { listTeam, searchTeam, getMember, createMember, updateMember, toggleStatus, deleteMember };
};
